import { createFileRoute, notFound } from '@tanstack/react-router'
import { History } from 'lucide-react'
import { useTranslations } from 'use-intl'

import { NotFoundPage } from '@/components/not-found'
import { PortableTextContent } from '@/components/portable-text'
import { ArticleToc } from '@/features/blog/components/article-sidebar'
import { extractToc } from '@/features/blog/toc'
import { pageHead, SITE_URL } from '@/lib/seo'
import { breadcrumbSchema, homeBreadcrumbItem } from '@/lib/structured-data'
import { sanityClient } from '@/sanity/client'
import { legalQuery } from '@/sanity/queries'

interface LegalPage {
  title: string
  body: never[]
  updatedAt?: string
  otherSlug?: string
}

export const Route = createFileRoute('/$locale/legal/$slug')({
  loader: async ({ params }) => {
    const page = await sanityClient.fetch<LegalPage | null>(
      legalQuery(params.locale),
      { slug: params.slug },
    )
    if (!page) throw notFound()
    return { page }
  },
  // Placed here (not on `/$locale`) so the leaf match is the notFound
  // boundary: only this match's own `component` gets bypassed, while
  // `/$locale`'s LocaleLayout (Header/Footer) stays resolved as an ancestor
  // and renders this in its Outlet.
  notFoundComponent: () => <NotFoundPage />,
  head: ({ loaderData, params }) => {
    const page = loaderData?.page
    const ownPath = `/legal/${params.slug}`
    const otherPath = page?.otherSlug ? `/legal/${page.otherSlug}` : undefined
    const altPaths =
      params.locale === 'en'
        ? { fr: otherPath, en: ownPath }
        : { fr: ownPath, en: otherPath }
    return pageHead({
      locale: params.locale,
      path: ownPath,
      title: page?.title ?? '',
      description: page?.title ?? '',
      altPaths,
      jsonLd: breadcrumbSchema([
        homeBreadcrumbItem(params.locale),
        { name: page?.title ?? '', url: `${SITE_URL}/${params.locale}${ownPath}` },
      ]),
    })
  },
  component: Legal,
})

function Legal() {
  const { page } = Route.useLoaderData()
  const { locale } = Route.useParams()
  const t = useTranslations()
  const toc = extractToc(page.body)
  const updatedDate = page.updatedAt
    ? new Date(page.updatedAt).toLocaleDateString(locale, {
        timeZone: 'Europe/Paris',
        dateStyle: 'long',
      })
    : null

  return (
    <>
      {/* Hero band: same abstract-3d background + header-pull as blog articles,
          no wordmark or borders. Fades into the page. */}
      <div className="relative isolate -mt-(--header-h) h-[130px] md:h-[280px] overflow-hidden pt-(--header-h)">
        <img
          src="/backgrounds/abstract-3d-light.webp"
          alt=""
          aria-hidden
          width={1920}
          height={1080}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="pointer-events-none absolute inset-0 -z-10 size-full scale-[1.3] object-cover object-[35%_85%]"
        />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-1/2 bg-gradient-to-t from-background to-transparent" />
      </div>

      <div className="mx-auto max-w-6xl px-6 pb-16 md:pb-24 lg:px-8">
        {/* Two-column grid with a sticky ToC only when the body has headings;
            otherwise a single centered column so there's no empty grid track. */}
        <div
          className={
            toc.length > 0
              ? 'grid gap-10 lg:grid-cols-[minmax(0,1fr)_18rem] lg:gap-14'
              : 'mx-auto max-w-3xl'
          }
        >
          <article className="min-w-0">
            <h1 className="font-serif text-3xl font-bold text-primary lg:text-4xl">
              {page.title}
            </h1>

            {updatedDate && (
              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <History className="size-4" />
                  {t('blog.updatedOn', { date: updatedDate })}
                </span>
              </div>
            )}

            {/* On mobile the sommaire sits inline above the body; on desktop it
                lives in the sticky sidebar (hidden here). */}
            <ArticleToc
              entries={toc}
              title={t('blog.toc')}
              className="mt-8 lg:hidden"
            />

            <div className="mt-8">
              <PortableTextContent value={page.body} />
            </div>
          </article>

          {toc.length > 0 && (
            <aside className="lg:sticky lg:top-28 lg:self-start">
              <ArticleToc
                entries={toc}
                title={t('blog.toc')}
                className="hidden lg:block"
              />
            </aside>
          )}
        </div>
      </div>
    </>
  )
}
